import type { Id, InventoryRow } from "@estelle/domain";

/** Stock on hand, in transit and low-stock count for one warehouse. */
export interface WarehouseInventorySummary {
  warehouseId: Id;
  warehouseName: string;
  warehouseCode: string;
  stockOnHand: number;
  inTransit: number;
  lowStockCount: number;
}

/**
 * Groups every stock row by warehouse (FR-DSH-4 / ticket #96).
 *
 * `lowStockRows` is a second array rather than a predicate run over `rows`
 * here, because the threshold comparison already lives in exactly one place
 * — `InventoryRepository.listLowStock` — and FR-INV-3's fallback-to-default
 * logic must not be reimplemented a second time where it could quietly
 * disagree with the Low Stock screen about which rows qualify.
 */
export function aggregateInventoryByWarehouse(
  rows: readonly InventoryRow[],
  lowStockRows: readonly InventoryRow[],
): WarehouseInventorySummary[] {
  interface Accumulator {
    name: string;
    code: string;
    stockOnHand: number;
    inTransit: number;
    lowStockCount: number;
  }

  const groups = new Map<Id, Accumulator>();

  function group(warehouse: InventoryRow["warehouse"]): Accumulator {
    let entry = groups.get(warehouse.id);
    if (!entry) {
      entry = {
        name: warehouse.name,
        code: warehouse.code,
        stockOnHand: 0,
        inTransit: 0,
        lowStockCount: 0,
      };
      groups.set(warehouse.id, entry);
    }
    return entry;
  }

  for (const row of rows) {
    const entry = group(row.warehouse);
    entry.stockOnHand += row.available;
    entry.inTransit += row.inTransit;
  }

  for (const row of lowStockRows) {
    group(row.warehouse).lowStockCount += 1;
  }

  return Array.from(groups.entries())
    .map(([warehouseId, entry]) => ({
      warehouseId,
      warehouseName: entry.name,
      warehouseCode: entry.code,
      stockOnHand: entry.stockOnHand,
      inTransit: entry.inTransit,
      lowStockCount: entry.lowStockCount,
    }))
    .sort((a, b) => a.warehouseName.localeCompare(b.warehouseName));
}
